import { useCallback, useEffect, useRef, useState } from 'react';

import type { SkillFile, SkillFileContent } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

type Status = 'idle' | 'loading' | 'success' | 'error';

export type ResourceKind = 'skill' | 'agent';

/**
 * Shared ViewModel for the file-browser tab that powers both the Skills view
 * and the Agents view. Loads the file tree at HEAD (or at a time-traveled
 * ref) and the contents of one selected file. When the API surface for the
 * two resource types is identical (it is — same shape, parameterized by
 * `/skills/...` vs `/agents/...`), one hook serves both.
 *
 * `defaultFile` controls which file is auto-selected when the tree first
 * loads — typically `SKILL.md` for skills and `AGENT.md` for agents.
 */
export function useResourceTree(
  kind: ResourceKind,
  resourceId: string | null,
  ref: string | null,
  options: { enabled?: boolean; defaultFile?: string } = {},
) {
  const apiClient = useApiClient();
  const { enabled = true, defaultFile } = options;
  const effectiveDefault = defaultFile ?? (kind === 'agent' ? 'AGENT.md' : 'SKILL.md');

  const [files, setFiles] = useState<SkillFile[]>([]);
  const [filesStatus, setFilesStatus] = useState<Status>('idle');
  const [filesError, setFilesError] = useState<string | undefined>();

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<SkillFileContent | null>(null);
  const [fileStatus, setFileStatus] = useState<Status>('idle');
  const [fileError, setFileError] = useState<string | undefined>();

  const filesGenerationRef = useRef(0);
  const fileGenerationRef = useRef(0);

  useEffect(() => {
    if (!resourceId || !enabled) {
      setFiles([]);
      setFilesStatus('idle');
      setSelectedPath(null);
      setFileContent(null);
      return;
    }
    const gen = ++filesGenerationRef.current;
    setFilesStatus('loading');
    const fetcher =
      kind === 'agent'
        ? apiClient.getAgentFiles(resourceId, ref ?? undefined)
        : apiClient.getSkillFiles(resourceId, ref ?? undefined);
    fetcher
      .then((data) => {
        if (gen !== filesGenerationRef.current) return;
        setFiles(data);
        setFilesStatus('success');
        setSelectedPath((prev) => {
          if (prev && data.some((f) => f.path === prev)) return prev;
          const preferred =
            data.find((f) => f.path === effectiveDefault) ?? data[0];
          return preferred?.path ?? null;
        });
      })
      .catch((err: unknown) => {
        if (gen !== filesGenerationRef.current) return;
        setFilesError(err instanceof Error ? err.message : 'Failed to load files');
        setFilesStatus('error');
      });
  }, [apiClient, kind, resourceId, ref, enabled, effectiveDefault]);

  useEffect(() => {
    if (!resourceId || !selectedPath || !enabled) {
      setFileContent(null);
      setFileStatus('idle');
      return;
    }
    const gen = ++fileGenerationRef.current;
    setFileStatus('loading');
    const fetcher =
      kind === 'agent'
        ? apiClient.getAgentFile(resourceId, selectedPath, ref ?? undefined)
        : apiClient.getSkillFile(resourceId, selectedPath, ref ?? undefined);
    fetcher
      .then((data) => {
        if (gen !== fileGenerationRef.current) return;
        setFileContent(data);
        setFileStatus('success');
      })
      .catch((err: unknown) => {
        if (gen !== fileGenerationRef.current) return;
        setFileError(err instanceof Error ? err.message : 'Failed to load file');
        setFileStatus('error');
      });
  }, [apiClient, kind, resourceId, selectedPath, ref, enabled]);

  const selectFile = useCallback((path: string) => {
    setSelectedPath(path);
  }, []);

  return {
    files,
    filesStatus,
    filesError,
    selectedPath,
    selectFile,
    fileContent,
    fileStatus,
    fileError,
  };
}
